import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import DataLoader from "../Spinner/DataLoader";
import PageInfoModal from "../PageInfoModal/PageInfoModal";

const css = `
  .gvt-wrapper {
    display: flex; flex-direction: column;
    font-family: 'Inter','Roboto',sans-serif;
    font-size: 12px; height: 100%; overflow: hidden;
  }
  .gvt-toolbar {
    display: flex; width: 100%; background: #f3f4f6;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    align-items: center; justify-content: space-between;
    padding: 4px 8px; margin-bottom: 8px; border-radius: 6px;
  }
  .gvt-toolbar-left  { display: flex; align-items: center; gap: 6px; }
  .gvt-toolbar-right { display: flex; align-items: center; gap: 4px; }
  .gvt-btn-refresh {
    display: flex; align-items: center; justify-content: center;
    background: #2563eb; color: #fff; border: none;
    border-radius: 6px; width: 28px; height: 28px; cursor: pointer; font-size: 13px;
  }
  .gvt-btn-refresh:hover { background: #1d4ed8; }
  .gvt-table-wrapper {
    background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    overflow: hidden; border-radius: 4px;
    flex: 1; display: flex; flex-direction: column; min-height: 0;
  }
  .gvt-table-scroll { overflow-x: auto; overflow-y: auto; flex: 1; min-height: 0; }
  .gvt-table-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
  .gvt-table-scroll::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 4px; }
  .gvt-table-scroll::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 4px; }
  .gvt-table-scroll::-webkit-scrollbar-thumb:hover { background: #60a5fa; }
  .gvt-data-table { width: 100%; border-collapse: collapse; font-size: 12px; color: #374151; }
  .gvt-data-table thead tr { background: #f3f4f6; position: sticky; top: 0; z-index: 5; }
  .gvt-data-table th {
    padding: 8px 12px; text-align: left; border-bottom: 1px solid #e5e7eb;
    font-weight: 700; font-size: 11px; text-transform: uppercase;
    white-space: nowrap; letter-spacing: 0.03em;
  }
  .gvt-data-table tbody tr { border-bottom: 1px solid #e5e7eb; transition: background 0.15s; }
  .gvt-data-table tbody tr:hover { background: #f9fafb; }
  .gvt-data-table td { padding: 6px 12px; white-space: nowrap; cursor: pointer; }
  .gvt-empty { text-align: center; padding: 40px 0; color: #9ca3af; font-size: 12px; }
  .gvt-spinner-wrap {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 40px 0; gap: 8px; color: #9ca3af;
  }
  .gvt-badge-count {
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(37,99,235,0.1); color: #2563eb;
    border-radius: 10px; padding: 1px 8px; font-size: 11px; font-weight: 700;
  }
`;

const PAGE_INFO_ITEMS = [
  {
    icon: "bi-people-fill",
    text: "Bu tabda qrupa aid tələbələrin valideyn məlumatları göstərilir.",
  },
  {
    icon: "bi-clipboard-plus",
    text: "Hər xanaya klikləyərək mətni kopyalama panelindəki sahəyə köçürə bilərsiniz.",
  },
  {
    icon: "bi-arrow-clockwise",
    text: "Məlumatları yeniləmək üçün yenilə düyməsindən istifadə edin.",
  },
];

const GroupParentsTab = ({ groupId, onCellClick }) => {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchParents = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [res] = await Promise.all([
        apiFetch(`${BASE_URL}/parents/group-student-parents/${groupId}`),
        new Promise((r) => setTimeout(r, 600)),
      ]);
      if (!res.ok) {
        setParents([]);
        return;
      }
      const data = await res.json();
      setParents(data?.list ?? []);
    } catch {
      setParents([]);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchParents();
  }, [fetchParents]);

  const handleCellClick = (val) => {
    onCellClick?.(val?.toString() ?? "");
  };

  return (
    <div className="gvt-wrapper">
      <style>{css}</style>

      <div className="gvt-toolbar">
        <div className="gvt-toolbar-left">
          <PageInfoModal title="Qrup Valideynləri" items={PAGE_INFO_ITEMS} />
          {parents.length > 0 && (
            <span className="gvt-badge-count">{parents.length} qeyd</span>
          )}
        </div>
        <div className="gvt-toolbar-right">
          <button
            className="gvt-btn-refresh"
            onClick={fetchParents}
            disabled={loading}
            title="Yenilə"
          >
            <i className="bi bi-arrow-clockwise" />
          </button>
        </div>
      </div>

      <div className="gvt-table-wrapper" style={{ position: "relative" }}>
        <DataLoader show={loading} />
        {!loading && parents.length === 0 ? (
          <div className="gvt-empty">
            Bu qrup üçün valideyn məlumatı tapılmadı.
          </div>
        ) : (
          <div className="gvt-table-scroll">
            <table className="gvt-data-table">
              <thead>
                <tr>
                  <th>Tələbə kodu</th>
                  <th>Tələbə</th>
                  <th>Valideyn kodu</th>
                  <th>Valideyn</th>
                  <th>Valideyn telefonu</th>
                  <th>İstifadəçi adı</th>
                  <th>Daxil olub</th>
                </tr>
              </thead>
              <tbody>
                {parents.map((row, i) => (
                  <tr key={`${row.parentId}-${row.studentId}-${i}`}>
                    <td onClick={() => handleCellClick(row.studentId)}>
                      {row.studentId ?? "—"}
                    </td>
                    <td onClick={() => handleCellClick(row.student)}>
                      <strong>{row.student ?? "—"}</strong>
                    </td>
                    <td onClick={() => handleCellClick(row.parentId)}>
                      {row.parentId ?? "—"}
                    </td>
                    <td onClick={() => handleCellClick(row.parent)}>
                      {row.parent ?? "—"}
                    </td>
                    <td onClick={() => handleCellClick(row.parentPhone)}>
                      {row.parentPhone ?? "—"}
                    </td>
                    <td onClick={() => handleCellClick(row.username)}>
                      {row.username ?? "—"}
                    </td>
                    <td
                      onClick={() =>
                        handleCellClick(row.isOnline ? "Bəli" : "Xeyr")
                      }
                    >
                      {row.isOnline ? "Bəli" : "Xeyr"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupParentsTab;
